import {
  AutoAwesomeOutlined,
  AutoStoriesOutlined,
  BusinessOutlined,
  CasesOutlined,
  FavoriteBorder,
  FlagOutlined,
  GroupAddOutlined,
  HomeOutlined,
  ListAltOutlined,
  LogoutOutlined,
  PaidOutlined,
  PeopleOutlined,
  PersonAddAltOutlined,
  PersonOutlineOutlined,
  PieChartOutlineOutlined,
  ReceiptLongOutlined,
  RequestPageOutlined,
  SettingsOutlined,
  StorefrontOutlined,
  SupportAgentOutlined,
  RadarOutlined,
  UploadFileOutlined,
  UpgradeOutlined,
  WorkspacePremiumOutlined,
  WorkspacesOutlined
} from '@mui/icons-material'

import { Account, BackofficePersona } from '@/domain/usecases'
import { i18nTranslationHelper } from '@/i18n'
import {
  makeSkillWebInstitutionsRedirectUrl,
  makeSkillWebManagerTeamRedirectUrl
} from '@/main/factories/http'
import PathConstants from '@/main/routes/paths'
import { AccountWithCustomProperties } from '@/presentation/contexts/auth'
import { showDependentRequestButton } from '@/presentation/helpers/dependent-request-helper'
import { showUpgradeRequestButton } from '@/presentation/helpers/upgrade-request-helper'

import { MenuSectionDefinition } from './types'

const { Routes } = PathConstants
const t = i18nTranslationHelper.getTComponents

/**
 * Roles object resolved from Account.getRoles() — single source of truth for all nav visibility.
 */
export type NavRoles = ReturnType<typeof Account.getRoles>

/**
 * Valores de feature flags necessários para montar a navegação.
 * Lidos via `useFeatureFlags` no componente e injetados aqui para
 * manter as funções puras e garantir reatividade a mudanças de flags.
 */
export type NavFlags = {
  isUserSpaceEnabled: boolean
  isLearningPreferencesEnabled: boolean
}

/**
 * Complete navigation config for the user menu (dropdown + mobile drawer).
 * Each section is null when not applicable to the current user's roles.
 *
 * Visão matrix (from product spec):
 *   home        → all roles
 *   helpCenter  → conditional on company config
 *   profile     → all roles (core items: dados, configurações; benefit items: only colaborador/dependente)
 *   team        → isTeamManager
 *   admin       → isCompanyManager (RH, RH+Colab)
 *   backoffice  → isInternalCompanyManager (SuperAdmin)
 *   logout      → all roles
 */
export type NavConfig = {
  home: MenuSectionDefinition | null
  helpCenter: MenuSectionDefinition | null
  profile: MenuSectionDefinition | null
  team: MenuSectionDefinition | null
  manager: MenuSectionDefinition | null
  logout: MenuSectionDefinition
}

export function buildNavConfig(
  account: AccountWithCustomProperties,
  roles: NavRoles,
  flags: NavFlags
): NavConfig {
  return {
    home: buildHomeItem(account),
    helpCenter: buildHelpCenterSection(account),
    profile: buildProfileSection(account, roles, flags),
    team: buildTeamSection(roles),
    manager: buildManagerSection(account),
    logout: buildLogoutSection()
  }
}

// ─── Section builders ────────────────────────────────────────────────────────

function buildHomeItem(account: AccountWithCustomProperties): MenuSectionDefinition | null {
  const hasActivePlan = Account.hasPassPlan(account)

  if (!hasActivePlan) {
    return null
  }

  return {
    items: [
      // path: '/' | page: 'Home'
      {
        key: 'home',
        route: {
          value: Routes.HOME.value,
          title: Routes.HOME.title,
          name: 'home'
        },
        icon: HomeOutlined,
        isVisible: true
      }
    ]
  }
}

function buildHelpCenterSection(
  account: AccountWithCustomProperties
): MenuSectionDefinition | null {
  if (!account?.helpCenterConfig?.hasLink) return null

  return {
    items: [
      // path: dynamic from account.helpCenterConfig.redirectTo | page: 'Central de Ajuda'
      {
        key: 'help_center',
        route: {
          title: t('helpCenterButtonApp.cta'),
          value: account.helpCenterConfig.redirectTo,
          name: 'help'
        },
        icon: SupportAgentOutlined,
        isVisible: true
      }
    ]
  }
}

/**
 * Profile section: core items (meus dados, configurações) are visible to all roles.
 * Desktop top bar counterpart: DesktopNavMenuMain — mirror changes there too.
 * Benefit items (meu espaço, inscrições, favoritos, etc.) are only visible to
 * colaborador and dependente account types — which naturally includes RH+Colab
 * (company managers whose accountType is COLLABORATOR).
 */
function buildProfileSection(
  account: AccountWithCustomProperties,
  roles: NavRoles,
  flags: NavFlags
): MenuSectionDefinition | null {
  const hasActivePlan = Account.hasPassPlan(account)

  if (!hasActivePlan) {
    return null
  }

  const isAddonRefundEnabled = !!account.addons?.refund.enabled
  const { isUserSpaceEnabled, isLearningPreferencesEnabled } = flags
  const isShowUpgradeRequestButton = showUpgradeRequestButton(account)
  const isShowDependentRequestButton = showDependentRequestButton(account)

  return {
    titleKey: 'navUserMenu.listTitle',
    items: [
      // Core — todos os perfis
      // path: '/user/profile' | page: 'Meus Dados'
      {
        key: 'user_profile',
        route: Routes.USER_PROFILE,
        icon: PersonOutlineOutlined,
        isVisible: true
      },

      // Benefício — colaborador e dependente
      // path: '/user/space' | page: 'Meu Espaço'
      {
        key: 'user_space',
        route: Routes.USER_SPACE,
        icon: WorkspacePremiumOutlined,
        isVisible: isUserSpaceEnabled && roles.hasBenefitMenu
      },

      // path: '/user/enrollments' | page: 'Minhas Inscrições'
      {
        key: 'user_enrollments',
        route: Routes.USER_ENROLLMENTS_V2,
        icon: AutoStoriesOutlined,
        isVisible: roles.hasBenefitMenu
      },

      // path: '/user/interest-areas-skills' | page: 'Habilidades, Interesses e Idiomas'
      {
        key: 'user_interest_areas_skills',
        route: Routes.USER_INSTEREST_AREAS_SKILLS,
        icon: AutoAwesomeOutlined,
        isVisible: roles.hasBenefitMenu
      },

      // path: '/user/learning-preferences' | page: 'Objetivo e Preferências'
      {
        key: 'user_learning_preferences',
        route: Routes.USER_LEARNING_PREFERENCES,
        icon: RadarOutlined,
        isVisible: isLearningPreferencesEnabled && roles.hasBenefitMenu
      },

      // path: '/user/favorites' | page: 'Favoritos'
      {
        key: 'user_favorites',
        route: Routes.FAVORITES,
        icon: FavoriteBorder,
        isVisible: roles.hasBenefitMenu
      },

      // Apenas colaborador (não dependente)
      // path: '/upgrade-request' | page: 'Upgrade' (mobile only)
      {
        key: 'upgrade_request',
        route: { ...Routes.UPGRADE_REQUEST, title: t('mobileUpgradeRequestButton.cta') },
        icon: UpgradeOutlined,
        className: 'only-mobile',
        isVisible: isShowUpgradeRequestButton && roles.isCollaborator
      },

      // path: '/dependent-request' | page: 'Adesão de Dependentes' (mobile only)
      {
        key: 'dependent_request',
        route: { ...Routes.DEPENDENT_REQUEST, title: t('mobileDependentRequestButton.cta') },
        icon: PersonAddAltOutlined,
        className: 'only-mobile',
        isVisible: isShowDependentRequestButton && roles.isCollaborator,
        onClick: () => sessionStorage.removeItem('dependentRequestForm')
      },

      // Colaborador, dependente e RH (se o addon estiver habilitado)
      // path: '/refund' | page: 'Reembolso' (mobile only)
      {
        key: 'user_refund',
        route: { ...Routes.REFUND, title: t('mobileUserRefundButton.cta') },
        icon: PaidOutlined,
        className: 'only-mobile',
        isVisible: isAddonRefundEnabled && (roles.hasBenefitMenu || roles.isCompanyManager)
      },

      // Core — todos os perfis
      // path: '/user/settings' | page: 'Configurações'
      {
        key: 'user_settings',
        route: Routes.USER_SETTINGS,
        icon: SettingsOutlined,
        isVisible: true
      }
    ]
  }
}

/**
 * Team section: visible only for team managers.
 * Redirects to legacy monolith — no new route exists yet.
 */
function buildTeamSection(roles: NavRoles): MenuSectionDefinition | null {
  if (!roles.isTeamManager) return null

  return {
    titleKey: 'navUserMenu.teamManagerListTitle',
    items: [
      // path: '/app/authorizations' (monolith) | page: 'Pedidos'
      {
        key: 'team_manager_authorizations',
        route: {
          value: makeSkillWebManagerTeamRedirectUrl(),
          title: t('navUserMenu.orderTitle'),
          name: 'monolith_team_manager_authorizations'
        },
        icon: ListAltOutlined,
        isVisible: true
      }
    ]
  }
}

/**
 * Manager section: Menu Administrativo (RH) + Menu Backoffice (SuperAdmin), unificados.
 * Desktop top bar counterpart: DesktopNavMenuAdmin — mirror changes there too.
 * Cada item tem isVisible controlado pela role correspondente.
 */
function buildManagerSection(account: AccountWithCustomProperties): MenuSectionDefinition | null {
  // Personas de backoffice (membership, não-exclusivo):
  //   COMPANY_ADMIN → Acesso RH (Company Manager)
  //   INTERNAL      → Acesso SuperAdmin (Internal Company Manager)
  const personas = Account.getPersonas(account)
  const isAdmin = personas.includes(BackofficePersona.COMPANY_ADMIN)
  const isBackoffice = personas.includes(BackofficePersona.INTERNAL)

  if (!isAdmin && !isBackoffice) return null

  return {
    titleKey: 'navUserMenu.companyManagerListTitle',
    items: [
      // Admin only: path '/backoffice/dashboard' | page: 'Dados'
      {
        key: 'admin_dashboard',
        route: Routes.COMPANY_MANAGER_DASHBOARD,
        icon: PieChartOutlineOutlined,
        isVisible: isAdmin
      },
      // Admin + Backoffice (shared): path '/backoffice/collaborators' | page: 'Colaboradores'
      {
        key: 'admin_collaborators',
        route: Routes.COMPANY_MANAGER_COLLABORATORS,
        icon: PeopleOutlined,
        availableIn: [Routes.COMPANY_MANAGER_EDIT_USER_PROFILE],
        isVisible: isAdmin || isBackoffice
      },
      // Admin only: path '/backoffice/dependent-user' | page: 'Dependentes'
      {
        key: 'admin_dependents',
        route: Routes.COMPANY_MANAGER_DEPENDENT_LIST,
        icon: GroupAddOutlined,
        availableIn: [
          Routes.COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE,
          Routes.COMPANY_MANAGER_HOLDER_SEARCH,
          Routes.COMPANY_MANAGER_DEPENDENT_REGISTRATION
        ],
        isVisible: isAdmin
      },
      // Admin + Backoffice (shared): path '/backoffice/authorizations' | page: 'Pedidos'
      {
        key: 'admin_authorizations',
        route: Routes.COMPANY_MANAGER_AUTHORIZATIONS,
        icon: ListAltOutlined,
        isVisible: isAdmin || isBackoffice
      },
      // Admin only: path '/backoffice/groups' | page: 'Grupos'
      // TEMP: nav quick-access disabled while the Grupos page is broken. The route
      // still exists, so direct navigation works; we just hide it from the menu.
      // Restore `isVisible: isAdmin` once the page is fixed.
      {
        key: 'admin_groups',
        route: Routes.COMPANY_MANAGER_GROUPS,
        icon: WorkspacesOutlined,
        isVisible: false
      },
      // RH: aponta pra tela nova de detalhe da própria empresa.
      {
        key: 'admin_company',
        route: {
          // `route` é construído eagerly pra todos os itens (antes do filtro isVisible).
          // Guard defensivo: usuário de backoffice sem empresa não quebra a navegação.
          value: account.company?.uniqueId
            ? Routes.INTERNAL_COMPANY_MANAGER_COMPANY_DETAIL.value.replace(
                ':id',
                account.company.uniqueId
              )
            : '',
          title: t('navUserMenu.companyTitle'),
          name: 'internal_company_detail'
        },
        icon: CasesOutlined,
        isVisible: isAdmin
      },
      // Admin only: dropdown Solicitações
      {
        key: 'admin_requests',
        icon: RequestPageOutlined,
        isVisible: isAdmin,
        titleKey: 'navUserMenu.requestsTitle',
        items: [
          // path: '/backoffice/upgrade-enrollment' | page: 'Solicitação de Upgrade'
          {
            key: 'admin_upgrade_enrollment',
            route: Routes.COMPANY_MANAGER_UPGRADE_ENROLLMENT,
            icon: UpgradeOutlined,
            isVisible: true
          },
          // path: '/backoffice/dependent-enrollment' | page: 'Adesão de Dependentes'
          {
            key: 'admin_dependent_enrollment',
            route: Routes.COMPANY_MANAGER_DEPENDENT_ENROLLMENT,
            icon: PersonAddAltOutlined,
            isVisible: true
          },
          // path: '/backoffice/scholarship-renewal' | page: 'Renovação de Bolsa'
          {
            key: 'admin_scholarship_renewal',
            route: Routes.COMPANY_MANAGER_SCHOLARSHIP_RENEWAL,
            icon: WorkspacePremiumOutlined,
            isVisible: true
          },
          // path: '/backoffice/requests/configuration' | page: 'Config. Solicitações'
          {
            key: 'admin_requests_configuration',
            route: Routes.COMPANY_MANAGER_REQUESTS_CONFIGURATION,
            icon: SettingsOutlined,
            isVisible: true
          }
        ]
      },
      // Backoffice only: path '/backoffice/invoice' | page: 'Notas Fiscais'
      {
        key: 'backoffice_invoice',
        route: Routes.INTERNAL_COMPANY_MANAGER_INVOICE,
        icon: ReceiptLongOutlined,
        isVisible: isBackoffice
      },
      // Backoffice only: monolith '/app/institutions' | page: 'Parceiros'
      {
        key: 'backoffice_partners',
        route: {
          value: makeSkillWebInstitutionsRedirectUrl(),
          title: t('navUserMenu.partnersTitle'),
          name: 'monolith_backoffice_partners'
        },
        icon: StorefrontOutlined,
        isVisible: isBackoffice
      },
      // Backoffice only: page: 'Empresas'
      {
        key: 'backoffice_companies',
        route: Routes.INTERNAL_COMPANY_MANAGER_COMPANIES,
        icon: BusinessOutlined,
        isVisible: isBackoffice
      },
      // Backoffice only: external catalog importer
      {
        key: 'backoffice_import_catalog',
        route: {
          value: 'https://unicoskill.app.n8n.cloud/form/catalog-importer',
          title: t('navUserMenu.importCatalogTitle'),
          name: 'backoffice_import_catalog'
        },
        icon: UploadFileOutlined,
        isVisible: isBackoffice
      },
      // Backoffice only: page: 'Fit Score'
      {
        key: 'backoffice_fit_score',
        route: Routes.INTERNAL_COMPANY_MANAGER_FIT_SCORE,
        icon: FlagOutlined,
        isVisible: isBackoffice
      }
    ]
  }
}

function buildLogoutSection(): MenuSectionDefinition {
  return {
    items: [
      // path: '/logout' | page: 'Sair'
      {
        key: 'logout',
        route: {
          value: Routes.LOGOUT.value,
          title: t('navUserMenu.logoutCta'),
          name: 'monolith_logout'
        },
        icon: LogoutOutlined,
        isVisible: true
      }
    ]
  }
}
